import { useEffect, useState } from "react";
import { api, fmtMoney, formatApiError, CATEGORIES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import PrintPageButton from "@/components/PrintPageButton";
import { BudgetTrendChart, BudgetTemplatesButton } from "@/components/BudgetExtras";

const SCOPES = [
    { value: "overall", label: "Overall (all cards)" },
    { value: "card", label: "Specific card" },
    { value: "category", label: "Expense category" },
    { value: "business_personal", label: "Business or Personal" },
];

const EMPTY = { name: "", scope: "overall", scope_value: "", period: "monthly", amount: 0, remarks: "" };

const STATUS_STYLES = {
    ok: "bg-emerald-50 border-emerald-200 text-emerald-700",
    watch: "bg-blue-50 border-blue-200 text-blue-700",
    warning: "bg-amber-50 border-amber-200 text-amber-700",
    critical: "bg-orange-50 border-orange-200 text-orange-700",
    exceeded: "bg-red-50 border-red-200 text-red-700",
};

export default function Budgets() {
    const { hasRole } = useAuth();
    const [cards, setCards] = useState([]);
    const [rows, setRows] = useState([]);
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
    const [open, setOpen] = useState(false);
    const [f, setF] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);

    const load = () => api.get("/budgets/progress", { params: { month } }).then((r) => setRows(r.data.budgets || []));
    useEffect(() => {
        api.get("/cards").then((r) => setCards(r.data));
    }, []);
    useEffect(() => { load(); }, [month]);

    const set = (k) => (v) => setF((s) => ({ ...s, [k]: v, ...(k === "scope" ? { scope_value: "" } : {}) }));
    const setInput = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...f, amount: Number(f.amount || 0) };
            if (f.scope === "overall") payload.scope_value = null;
            if (editingId) {
                await api.patch(`/budgets/${editingId}`, payload);
                toast.success("Budget updated");
            } else {
                await api.post("/budgets", payload);
                toast.success("Budget created");
            }
            setOpen(false);
            setEditingId(null);
            setF(EMPTY);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const openAdd = () => { setEditingId(null); setF(EMPTY); setOpen(true); };
    const openEdit = (row) => {
        setEditingId(row.id);
        setF({
            name: row.name || "",
            scope: row.scope || "overall",
            scope_value: row.scope_value || "",
            period: row.period || "monthly",
            amount: row.amount || 0,
            remarks: row.remarks || "",
        });
        setOpen(true);
    };

    const del = async (id) => {
        if (!window.confirm("Delete this budget?")) return;
        await api.delete(`/budgets/${id}`);
        load();
    };

    const cardMap = Object.fromEntries(cards.map((c) => [c.id, c]));
    const scopeLabel = (b) => {
        if (b.scope === "overall") return "All cards";
        if (b.scope === "card") return cardMap[b.scope_value] ? `${cardMap[b.scope_value].bank_name} · •••• ${cardMap[b.scope_value].last_4}` : b.scope_value;
        return b.scope_value;
    };

    return (
        <div data-testid="budgets-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow">Budgets</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Guard rails.</h1>
                    <p className="text-sm text-stone-500 mt-3">Monthly caps by card, category, or business/personal.</p>
                </div>
                <div className="flex items-center gap-3">
                    <PrintPageButton />
                    <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="budgets-month" />
                    <BudgetTemplatesButton onDone={load} />
                    {hasRole("admin", "finance") && (
                        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setF(EMPTY); } }}>
                            <DialogTrigger asChild>
                                <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="add-budget-btn" onClick={openAdd}><Plus size={16} className="mr-1.5" /> New budget</Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle className="font-display text-2xl">{editingId ? "Edit budget" : "New budget"}</DialogTitle></DialogHeader>
                                <form onSubmit={submit} className="space-y-3">
                                    <div><Label className="eyebrow">Name</Label><Input required value={f.name} onChange={setInput("name")} className="mt-1.5" placeholder="e.g. Travel budget FY26" /></div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label className="eyebrow">Scope</Label>
                                            <Select value={f.scope} onValueChange={set("scope")}>
                                                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                                <SelectContent>{SCOPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="eyebrow">Amount / month</Label>
                                            <Input required type="number" value={f.amount} onChange={setInput("amount")} className="mt-1.5" />
                                        </div>
                                    </div>
                                    {f.scope === "card" && (
                                        <div>
                                            <Label className="eyebrow">Card</Label>
                                            <Select value={f.scope_value} onValueChange={set("scope_value")}>
                                                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select card" /></SelectTrigger>
                                                <SelectContent>{cards.map((c) => <SelectItem key={c.id} value={c.id}>{c.bank_name} · •••• {c.last_4}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                    {f.scope === "category" && (
                                        <div>
                                            <Label className="eyebrow">Category</Label>
                                            <Select value={f.scope_value} onValueChange={set("scope_value")}>
                                                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select category" /></SelectTrigger>
                                                <SelectContent className="max-h-64">{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                    {f.scope === "business_personal" && (
                                        <div>
                                            <Label className="eyebrow">Business or Personal</Label>
                                            <Select value={f.scope_value} onValueChange={set("scope_value")}>
                                                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                                <SelectContent><SelectItem value="Business">Business</SelectItem><SelectItem value="Personal">Personal</SelectItem></SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                    <div><Label className="eyebrow">Remarks</Label><Textarea rows={2} value={f.remarks} onChange={setInput("remarks")} className="mt-1.5" /></div>
                                    <Button type="submit" className="w-full bg-stone-900 hover:bg-stone-800 text-white">{editingId ? "Save changes" : "Create budget"}</Button>
                                </form>
                            </DialogContent>
                        </Dialog>
                    )}
                </div>
            </div>

            <BudgetTrendChart />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {rows.map((b) => {
                    const styleCls = STATUS_STYLES[b.status] || STATUS_STYLES.ok;
                    return (
                        <div key={b.id} className="kpi-card" data-testid={`budget-card-${b.id}`}>
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="eyebrow">{b.scope}</div>
                                    <div className="font-display text-2xl text-stone-900 mt-1">{b.name}</div>
                                    <div className="text-xs text-stone-500 mt-0.5">{scopeLabel(b)}</div>
                                </div>
                                <span className={`status-pill ${styleCls}`}>{b.status}</span>
                            </div>
                            <div className="mt-4">
                                <div className="flex items-baseline justify-between mb-1">
                                    <span className="text-sm text-stone-500">Spent</span>
                                    <span className="tabular-nums text-sm">{fmtMoney(b.spent)} / {fmtMoney(b.amount)}</span>
                                </div>
                                <div className="h-2 w-full bg-stone-100 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full ${b.pct >= 100 ? "bg-red-500" : b.pct >= 90 ? "bg-orange-500" : b.pct >= 75 ? "bg-amber-500" : b.pct >= 50 ? "bg-blue-500" : "bg-emerald-500"}`}
                                        style={{ width: `${Math.min(100, b.pct)}%` }}
                                    />
                                </div>
                                <div className="flex justify-between text-[11px] text-stone-500 mt-1.5">
                                    <span>{b.pct}% used</span>
                                    <span>{fmtMoney(b.balance)} left</span>
                                </div>
                            </div>
                            {hasRole("admin", "finance") && (
                                <div className="mt-3 flex items-center gap-3">
                                    <button onClick={() => openEdit(b)} className="text-[11px] inline-flex items-center gap-1 text-stone-500 hover:text-stone-900" data-testid={`budget-edit-${b.id}`}>
                                        <Pencil size={11} /> Edit
                                    </button>
                                    <button onClick={() => del(b.id)} className="text-[11px] underline text-stone-400 hover:text-red-600" data-testid={`budget-delete-${b.id}`}>Deactivate</button>
                                </div>
                            )}
                        </div>
                    );
                })}
                {rows.length === 0 && (
                    <div className="col-span-2 border border-dashed border-stone-300 rounded-lg p-14 text-center text-stone-500">
                        No budgets set. Create one to start tracking overspend.
                    </div>
                )}
            </div>
        </div>
    );
}
